import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiList, FiX, FiChevronRight } from 'react-icons/fi';

const TableOfContents = ({ sections = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => 
        document.getElementById(section.id)
      ).filter(Boolean);

      let current = '';
      for (const element of sectionElements) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= 100) {
          current = element.id;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      setIsOpen(false);
    }
  };

  if (sections.length === 0) return null;

  return (
    <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-30">
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 bg-green-500 text-white rounded-full shadow-brand-lg border border-green-400 flex items-center justify-center transition-colors hover:bg-green-600"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Table of Contents"
      >
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {isOpen ? <FiX className="w-5 h-5" /> : <FiList className="w-5 h-5" />}
        </motion.div>
      </motion.button>

      {/* Table of Contents Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-0 mr-16 w-64 bg-white rounded-lg shadow-brand-lg border border-primary-200 p-4"
          >
            <h3 className="text-sm font-semibold font-heading text-neutral-800 mb-3 flex items-center">
              <FiList className="w-4 h-4 mr-2" />
              Table of Contents
            </h3>
            
            <nav className="space-y-1">
              {sections.map((section, index) => (
                <motion.button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left py-2 px-3 rounded-md transition-all text-sm font-accent flex items-center justify-between group ${
                    activeSection === section.id
                      ? 'bg-secondary-50 text-secondary-700 border-l-2 border-secondary-500'
                      : 'text-neutral-600 hover:text-secondary-600 hover:bg-primary-50'
                  }`}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="truncate">{section.title}</span>
                  <FiChevronRight className={`w-3 h-3 transition-transform ${
                    activeSection === section.id ? 'text-secondary-500' : 'group-hover:translate-x-1'
                  }`} />
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TableOfContents;